import {
  Container,
  SpaceBetween,
  Button,
  Header,
  Box,
} from "@amzn/awsui-components-react/polaris";

import { FormFieldConfigType } from "src/forms/types";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { setSkipToStep } from "../../components/wizard/wizardSlice";
import React from "react";
import { ReviewPageContentsProps } from "../reviewPage/ReviewPage";
import {
  BUSINESS_DETAILS_CONSTANTS,
  CLAIM_DETAILS_CONSTANTS,
  INCIDENT_DETAILS_CONSTANTS,
  SHIPMENT_DETAILS_CONSTANTS,
  FILE_ATTACHMENTS_CONSTANTS,
} from "./constants";
import { CARGO_STEP_NAMES } from "../../app/Cargo/constants";
import { CARGO_STAGES } from "../../app/Cargo/types";
interface StepContentsProps {
  stepHeader: string;
  stepNumber: number;
  content: React.ReactElement;
}
const StepContents = ({
  stepHeader,
  stepNumber,
  content,
}: StepContentsProps) => {
  const dispatch = useAppDispatch();

  const setActiveStepIndex = (step: number) => {
    dispatch(setSkipToStep({ value: step }));
  };
  return (
    <SpaceBetween size="xs">
      <Header
        variant="h3"
        headingTagOverride="h2"
        actions={
          <Button
            onClick={() => setActiveStepIndex(stepNumber)}
            ariaLabel={`Edit, press this button to go back to Step ${
              stepNumber + 1
            }: ${stepHeader}`}
          >
            Edit
          </Button>
        }
      >
        {`Step ${stepNumber + 1}`}
      </Header>
      <Container
        header={
          <Header variant="h2" headingTagOverride="h3">
            {stepHeader}
          </Header>
        }
      >
        {content}
      </Container>
    </SpaceBetween>
  );
};
const CargoReviewPageContents = ({
  allFormConfigs,
  renderFieldGroup,
  renderFileList,
}: ReviewPageContentsProps) => {
  //Redux Forms state
  const { allFormStates } = useAppSelector((state: RootState) => state.forms);
  const { currentStage } = useAppSelector((state: RootState) => state.cargoApp);

  const getFieldGroupFields = (
    fieldGroup: FormFieldConfigType[],
    innerFieldName: string
  ): FormFieldConfigType[] => {
    return (
      fieldGroup.find(
        (field: FormFieldConfigType) => field.name == innerFieldName
      )?.fields ?? []
    );
  };
  const cargoStepNamesStage1 = CARGO_STEP_NAMES[CARGO_STAGES.STAGE1];
  const cargoStepNamesStage2 = CARGO_STEP_NAMES[CARGO_STAGES.STAGE2];
  const cargoStepNamesStage3 = CARGO_STEP_NAMES[CARGO_STAGES.STAGE3];

  if (currentStage === CARGO_STAGES.STAGE1) {
    const businessDetailsFields =
      allFormConfigs[BUSINESS_DETAILS_CONSTANTS.FORM_NAME][0].fields ?? [];
    const shipmentDetailsFields =
      allFormConfigs[SHIPMENT_DETAILS_CONSTANTS.FORM_NAME][0].fields ?? [];
    const incidentDetailsFields =
      allFormConfigs[INCIDENT_DETAILS_CONSTANTS.FORM_NAME][0].fields ?? [];
    return (
      <Box margin={{ bottom: "l" }}>
        <SpaceBetween size="xxl">
          <StepContents
            stepHeader={BUSINESS_DETAILS_CONSTANTS.FORM_HEADER}
            stepNumber={cargoStepNamesStage1.indexOf(
              BUSINESS_DETAILS_CONSTANTS.FORM_NAME
            )}
            content={
              <SpaceBetween size="m">
                {renderFieldGroup(
                  "",
                  businessDetailsFields,
                  allFormStates[BUSINESS_DETAILS_CONSTANTS.FORM_NAME],
                  BUSINESS_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField: BUSINESS_DETAILS_CONSTANTS.BUSINESS_UNIT,
                    stopBeforeField:
                      BUSINESS_DETAILS_CONSTANTS.POINT_OF_CONTACT_DETAILS,
                  }
                )}
                {renderFieldGroup(
                  "Point of Contact",
                  getFieldGroupFields(
                    businessDetailsFields,
                    BUSINESS_DETAILS_CONSTANTS.POINT_OF_CONTACT_DETAILS
                  ),
                  allFormStates[BUSINESS_DETAILS_CONSTANTS.FORM_NAME],
                  BUSINESS_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField:
                      BUSINESS_DETAILS_CONSTANTS.POINT_OF_CONTACT_NAME,
                  }
                )}
                {renderFieldGroup(
                  "Additional Contact Email Addresses",
                  getFieldGroupFields(
                    businessDetailsFields,
                    BUSINESS_DETAILS_CONSTANTS.ADDITIONAL_CONTACT_EMAIL_ADDRESSES
                  ),
                  allFormStates[BUSINESS_DETAILS_CONSTANTS.FORM_NAME],
                  BUSINESS_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField:
                      BUSINESS_DETAILS_CONSTANTS.ADDITIONAL_CONTACT_EMAIL_ADDRESS_0,
                  }
                )}
                {renderFieldGroup(
                  "Company Address",
                  getFieldGroupFields(
                    businessDetailsFields,
                    BUSINESS_DETAILS_CONSTANTS.COMPANY_ADDRESS
                  ),
                  allFormStates[BUSINESS_DETAILS_CONSTANTS.FORM_NAME],
                  BUSINESS_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField:
                      BUSINESS_DETAILS_CONSTANTS.COMPANY_ADDRESS_LINE_1,
                    expandable: true,
                    columns: 1,
                  }
                )}
                {renderFieldGroup(
                  "",
                  businessDetailsFields,
                  allFormStates[BUSINESS_DETAILS_CONSTANTS.FORM_NAME],
                  BUSINESS_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField: BUSINESS_DETAILS_CONSTANTS.COUNTRY_ID,
                  }
                )}
              </SpaceBetween>
            }
          />
          <StepContents
            stepHeader={SHIPMENT_DETAILS_CONSTANTS.FORM_HEADER}
            stepNumber={cargoStepNamesStage1.indexOf(
              SHIPMENT_DETAILS_CONSTANTS.FORM_NAME
            )}
            content={
              <SpaceBetween size="m">
                {renderFieldGroup(
                  "Carrier Details",
                  getFieldGroupFields(
                    shipmentDetailsFields,
                    SHIPMENT_DETAILS_CONSTANTS.CARRIER_DETAILS
                  ),
                  allFormStates[SHIPMENT_DETAILS_CONSTANTS.FORM_NAME],
                  SHIPMENT_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField: SHIPMENT_DETAILS_CONSTANTS.CARRIER_ID,
                  }
                )}
                {renderFieldGroup(
                  "",
                  shipmentDetailsFields,
                  allFormStates[SHIPMENT_DETAILS_CONSTANTS.FORM_NAME],
                  SHIPMENT_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField: SHIPMENT_DETAILS_CONSTANTS.PNC_TIMELINE_MET,
                    stopBeforeField:
                      SHIPMENT_DETAILS_CONSTANTS.MODE_OF_TRANSPORT_DETAILS,
                  }
                )}
                {renderFieldGroup(
                  "Mode of Transport Details",
                  getFieldGroupFields(
                    shipmentDetailsFields,
                    SHIPMENT_DETAILS_CONSTANTS.MODE_OF_TRANSPORT_DETAILS
                  ),
                  allFormStates[SHIPMENT_DETAILS_CONSTANTS.FORM_NAME],
                  SHIPMENT_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField: SHIPMENT_DETAILS_CONSTANTS.MODE_OF_TRANSPORT,
                  }
                )}
                {renderFieldGroup(
                  "Bill of Lading Details",
                  getFieldGroupFields(
                    shipmentDetailsFields,
                    SHIPMENT_DETAILS_CONSTANTS.BILL_OF_LADING_DETAILS
                  ),
                  allFormStates[SHIPMENT_DETAILS_CONSTANTS.FORM_NAME],
                  SHIPMENT_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField:
                      SHIPMENT_DETAILS_CONSTANTS.BILL_OF_LADING_NUMBER,
                  }
                )}
                {renderFieldGroup(
                  "",
                  shipmentDetailsFields,
                  allFormStates[SHIPMENT_DETAILS_CONSTANTS.FORM_NAME],
                  SHIPMENT_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField: SHIPMENT_DETAILS_CONSTANTS.CMR_RESERVED,
                    stopBeforeField:
                      SHIPMENT_DETAILS_CONSTANTS.ORIGINATION_DETAILS,
                  }
                )}
                {renderFieldGroup(
                  "Origination Details",
                  getFieldGroupFields(
                    shipmentDetailsFields,
                    SHIPMENT_DETAILS_CONSTANTS.ORIGINATION_DETAILS
                  ),
                  allFormStates[SHIPMENT_DETAILS_CONSTANTS.FORM_NAME],
                  SHIPMENT_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField: SHIPMENT_DETAILS_CONSTANTS.LANE_ORIGINATION,
                    columns: 1,
                    expandable: true,
                  }
                )}
                {renderFieldGroup(
                  "Destination Details",
                  getFieldGroupFields(
                    shipmentDetailsFields,
                    SHIPMENT_DETAILS_CONSTANTS.DESTINATION_DETAILS
                  ),
                  allFormStates[SHIPMENT_DETAILS_CONSTANTS.FORM_NAME],
                  SHIPMENT_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField: SHIPMENT_DETAILS_CONSTANTS.LANE_DESTINATION,
                    columns: 1,
                    expandable: true,
                  }
                )}
                {renderFieldGroup(
                  "",
                  shipmentDetailsFields,
                  allFormStates[SHIPMENT_DETAILS_CONSTANTS.FORM_NAME],
                  SHIPMENT_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField: SHIPMENT_DETAILS_CONSTANTS.CARGO_DESCRIPTION,
                    stopBeforeField: SHIPMENT_DETAILS_CONSTANTS.INVOICE_DETAILS,
                    columns: 1,
                  }
                )}
                {renderFieldGroup(
                  "Invoice Details",
                  getFieldGroupFields(
                    shipmentDetailsFields,
                    SHIPMENT_DETAILS_CONSTANTS.INVOICE_DETAILS
                  ),
                  allFormStates[SHIPMENT_DETAILS_CONSTANTS.FORM_NAME],
                  SHIPMENT_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField: SHIPMENT_DETAILS_CONSTANTS.INVOICE_NUMBER,
                    columns: 1,
                    expandable: true,
                  }
                )}
              </SpaceBetween>
            }
          />
          <StepContents
            stepHeader={INCIDENT_DETAILS_CONSTANTS.FORM_HEADER}
            stepNumber={cargoStepNamesStage1.indexOf(
              INCIDENT_DETAILS_CONSTANTS.FORM_NAME
            )}
            content={
              <SpaceBetween size="m">
                {renderFieldGroup(
                  "",
                  incidentDetailsFields,
                  allFormStates[INCIDENT_DETAILS_CONSTANTS.FORM_NAME],
                  INCIDENT_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField: INCIDENT_DETAILS_CONSTANTS.INCIDENT_DATE,
                    stopBeforeField:
                      INCIDENT_DETAILS_CONSTANTS.CLAIM_TYPE_DETAILS,
                  }
                )}
                {renderFieldGroup(
                  "",
                  getFieldGroupFields(
                    incidentDetailsFields,
                    INCIDENT_DETAILS_CONSTANTS.CLAIM_TYPE_DETAILS
                  ),
                  allFormStates[INCIDENT_DETAILS_CONSTANTS.FORM_NAME],
                  INCIDENT_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField: INCIDENT_DETAILS_CONSTANTS.CLAIM_TYPE_ID,
                  }
                )}
                {renderFieldGroup(
                  "",
                  getFieldGroupFields(
                    incidentDetailsFields,
                    INCIDENT_DETAILS_CONSTANTS.TOTAL_CLAIM_CURRENCY_DETAILS
                  ),
                  allFormStates[INCIDENT_DETAILS_CONSTANTS.FORM_NAME],
                  INCIDENT_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField:
                      INCIDENT_DETAILS_CONSTANTS.TOTAL_CLAIM_CURRENCY_CODE,
                  }
                )}
                {renderFieldGroup(
                  "",
                  incidentDetailsFields,
                  allFormStates[INCIDENT_DETAILS_CONSTANTS.FORM_NAME],
                  INCIDENT_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField:
                      INCIDENT_DETAILS_CONSTANTS.LOCATION_OF_INCIDENT,
                    stopBeforeField: INCIDENT_DETAILS_CONSTANTS.CONTACT_DETAILS,
                  }
                )}
                {renderFieldGroup(
                  "Contact Details",
                  getFieldGroupFields(
                    incidentDetailsFields,
                    INCIDENT_DETAILS_CONSTANTS.CONTACT_DETAILS
                  ),
                  allFormStates[INCIDENT_DETAILS_CONSTANTS.FORM_NAME],
                  INCIDENT_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField: INCIDENT_DETAILS_CONSTANTS.CONTACT_NAME,
                    expandable: true,
                    columns: 1,
                  }
                )}
                {renderFieldGroup(
                  "",
                  incidentDetailsFields,
                  allFormStates[INCIDENT_DETAILS_CONSTANTS.FORM_NAME],
                  INCIDENT_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField:
                      INCIDENT_DETAILS_CONSTANTS.ADDITIONAL_INFORMATION,
                    columns: 1,
                  }
                )}
              </SpaceBetween>
            }
          />
        </SpaceBetween>
      </Box>
    );
  }
  if (currentStage === CARGO_STAGES.STAGE2) {
    const claimDetailsFields =
      allFormConfigs[CLAIM_DETAILS_CONSTANTS.FORM_NAME][0].fields ?? [];
    return (
      <Box margin={{ bottom: "l" }}>
        <SpaceBetween size="xxl">
          <StepContents
            stepHeader={CLAIM_DETAILS_CONSTANTS.FORM_HEADER}
            stepNumber={cargoStepNamesStage2.indexOf(
              CLAIM_DETAILS_CONSTANTS.FORM_NAME
            )}
            content={
              <SpaceBetween size="m">
                {renderFieldGroup(
                  "",
                  claimDetailsFields,
                  allFormStates[CLAIM_DETAILS_CONSTANTS.FORM_NAME],
                  CLAIM_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField: CLAIM_DETAILS_CONSTANTS.SINGLE_ITEM_CLAIM,
                    stopBeforeField:
                      CLAIM_DETAILS_CONSTANTS.SINGLE_ITEM_DETAILS_GROUP,
                  }
                )}
                {renderFieldGroup(
                  "",
                  getFieldGroupFields(
                    claimDetailsFields,
                    CLAIM_DETAILS_CONSTANTS.SINGLE_ITEM_DETAILS_GROUP
                  ),
                  allFormStates[CLAIM_DETAILS_CONSTANTS.FORM_NAME],
                  CLAIM_DETAILS_CONSTANTS.FORM_NAME,
                  {
                    startAtField: CLAIM_DETAILS_CONSTANTS.QUANTITY,
                  }
                )}
                {renderFileList(
                  allFormStates[CLAIM_DETAILS_CONSTANTS.FORM_NAME],
                  claimDetailsFields
                )}
              </SpaceBetween>
            }
          />
        </SpaceBetween>
      </Box>
    );
  }
  const fileAttachmentsFields =
    allFormConfigs[FILE_ATTACHMENTS_CONSTANTS.FORM_NAME][0].fields ?? [];
  return (
    <Box margin={{ bottom: "l" }}>
      <SpaceBetween size="xxl">
        <StepContents
          stepHeader={FILE_ATTACHMENTS_CONSTANTS.FORM_HEADER}
          stepNumber={cargoStepNamesStage3.indexOf(
            FILE_ATTACHMENTS_CONSTANTS.FORM_NAME
          )}
          content={
            <SpaceBetween size="m">
              {renderFileList(
                allFormStates[FILE_ATTACHMENTS_CONSTANTS.FORM_NAME],
                fileAttachmentsFields
              )}
            </SpaceBetween>
          }
        />
      </SpaceBetween>
    </Box>
  );
};

export default CargoReviewPageContents;
